import { useContext, useState } from 'react';

import { Button, Card, Notice, TextLink } from '@upside/flux-ui';

import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AppContext } from '../../context/App';

function LinkExpiredContent({
  t,
  onSubmit,
  isSubmitting,
  error,
  isNewUser,
}: {
  t: TFunction<'linkExpired'>;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  error?: { message: string; disableButton: boolean };
  isNewUser: boolean;
}) {
  return (
    <>
      <div className="flex flex-col gap-4">
        <h1 className="text-center text-2xl font-medium text-foreground">
          {t('title')}
        </h1>

        {error && (
          <Notice intent="error" className="w-full">
            <Notice.Content>
              <Notice.Icon />
              <Notice.Caption>{error.message}</Notice.Caption>
            </Notice.Content>
          </Notice>
        )}

        <p>{t(isNewUser ? 'newUserDescription' : 'description')}</p>
      </div>

      <Button
        className="w-full items-center justify-center"
        loading={isSubmitting}
        size="lg"
        intent="primary"
        type="submit"
        onClick={onSubmit}
        disabled={isSubmitting || (error && error.disableButton)}
      >
        <Button.Label>{t('buttonText')}</Button.Label>
      </Button>
    </>
  );
}

function EmailSentContent({ t }: { t: TFunction<'linkExpired'> }) {
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-center text-2xl font-medium text-foreground">
        {t('emailSentTitle')}
      </h1>
      <p className="text-center">{t('emailSentDescription')}</p>
    </div>
  );
}

export function LinkExpired({
  isNewUser,
  email,
}: {
  isNewUser: boolean;
  email: string;
}) {
  const context = useContext(AppContext);
  const { t } = useTranslation('linkExpired');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<
    { message: string; disableButton: boolean } | undefined
  >();
  const [emailSent, setEmailSent] = useState(false);

  const onSubmit = async () => {
    setLoading(true);
    setError(undefined);
    try {
      if (context && context.authentication.triggerForgottenPassword) {
        await context.authentication.triggerForgottenPassword({ email });
        setEmailSent(true);
        setLoading(false);
      }
    } catch (err: any) {
      const { message } = err;
      if (message.includes('429')) {
        setError({ message: t('errorTooManyRequests'), disableButton: true });
      } else {
        setError({ message: t('error'), disableButton: false });
      }
      setLoading(false);
    }
  };

  return (
    <div className="my-auto flex flex-col items-center gap-12">
      <Card className="relative flex w-full max-w-md flex-col gap-6 rounded-xl p-8 text-center shadow-md">
        {emailSent ? (
          <EmailSentContent t={t} />
        ) : (
          <LinkExpiredContent
            t={t}
            onSubmit={onSubmit}
            isSubmitting={loading}
            error={error}
            isNewUser={isNewUser}
          />
        )}
      </Card>
      <TextLink asChild className="z-0">
        <Link to="/login">
          <TextLink.Label>{t('backToLogin')}</TextLink.Label>
        </Link>
      </TextLink>
    </div>
  );
}
